import './RestoreUserConfirmationModal.scss';
import React, {useContext, useEffect, useMemo, useState} from "react";
import {get} from "lodash";
import {Modal} from "../../Smart/Modal/Modal";
import {Button} from "../../Form/Button/Button";
import {buttonColors} from "../../Pickers/InputTheme";
import {DataTable} from "../../DataTable/DataTable";
import {ScrollableListCell} from "../../../Utils/ScrollableListCell/ScrollableListCell";
import {GridReactContext} from "../../../Utils/DataGridWrapper/GridContext";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {restoreDeletedUsersAPI} from "../../../Services/api";

/**
 * Two-view modal for bulk-restoring soft-deleted users.
 *
 * Confirmation view: lists the selected users (with their organizations) and asks for
 * a Cancel/Confirm decision. Results view: shown after the API resolves — displays a
 * success/warning banner, a table of restored users, and a skipped-users section
 * (with an expandable explanation) when any per-user restore failed.
 *
 * Mirrors the structure of `DeleteUserConfirmationModal` so the two flows feel the same.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is visible.
 * @param {Function} props.onClose - Closes the modal without firing success.
 * @param {Function} props.onSuccess - Fired when the user closes the Results view AFTER at least one user was restored.
 * @returns {JSX.Element}
 */
export const RestoreUserConfirmationModal = ({open, onClose, onSuccess}) => {
    const {i18n} = useContext(I18nReactContext);
    const gridContext = useContext(GridReactContext);

    const [restoring, setRestoring] = useState(false);
    const [error, setError] = useState(null);
    const [restoreResult, setRestoreResult] = useState(null);
    const [isExplanationExpanded, setIsExplanationExpanded] = useState(false);

    const selectionModel = useMemo(() => gridContext.selectionModel || [], [gridContext.selectionModel]);
    const usersToRestoreWithData = useMemo(() => gridContext.selectedRows || [], [gridContext.selectedRows]);

    // Reset transient state whenever the modal is closed so reopen starts fresh.
    // Also clear `restoring` in case the modal was dismissed (backdrop/Esc) mid-request —
    // otherwise the next open could render with the disabled "Restoring..." button.
    useEffect(() => {
        if (!open) {
            setError(null);
            setRestoreResult(null);
            setIsExplanationExpanded(false);
            setRestoring(false);
        }
    }, [open]);

    const onConfirm = async () => {
        try {
            setRestoring(true);
            setError(null);
            const response = await restoreDeletedUsersAPI({userIds: selectionModel});
            setRestoreResult(response);
            // Refresh the deleted-users table immediately so the restored rows disappear.
            if (response.restored > 0 && gridContext.triggerTableRefresh) {
                gridContext.triggerTableRefresh();
            }
            // Don't auto-close — let the user review the results.
        } catch (e) {
            console.error('Bulk restore users failed:', e);
            setError(i18n.translate('restoreUsersModal.restoreError'));
        } finally {
            setRestoring(false);
        }
    };

    const handleClose = () => {
        // Only forward success if at least one user actually got restored.
        if (restoreResult && restoreResult.restored > 0 && onSuccess) {
            onSuccess();
            return;
        }
        onClose();
    };

    const showConfirmation = !restoreResult;
    const showResults = restoreResult !== null;

    const getResultMessage = () => {
        if (!restoreResult) return '';
        const {restored} = restoreResult;
        if (restored > 0) {
            return i18n.translate('restoreUsersModal.successMessage', {context: {restored}});
        }
        return i18n.translate('restoreUsersModal.noRestoredMessage');
    };

    // Base columns shared by all three tables (confirmation + restored + skipped).
    const baseColumns = useMemo(() => [
        {key: 'firstName', label: i18n.translate('restoreUsersModal.firstName'), width: 150},
        {key: 'lastName', label: i18n.translate('restoreUsersModal.lastName'), width: 150},
        {key: 'email', label: i18n.translate('restoreUsersModal.email'), width: 300},
        {key: 'employeeId', label: i18n.translate('restoreUsersModal.employeeID'), width: 150},
        {key: 'idNumber', label: i18n.translate('restoreUsersModal.idNumber'), width: 150}
    ], [i18n]);

    // Confirmation view adds an organizations column — uses ScrollableListCell for overflow.
    const confirmationColumns = useMemo(() => [
        ...baseColumns,
        {
            key: 'organizations',
            label: i18n.translate('restoreUsersModal.organizations'),
            width: 300,
            render: (user) => {
                const organizations = get(user, 'row.organizations', []);
                if (organizations.length === 0) {
                    return '-';
                }
                const organizationNames = organizations.map((org) => get(org, 'orgName', '')).join(', ');
                return <ScrollableListCell value={organizationNames}/>;
            }
        }
    ], [baseColumns, i18n]);

    // Skipped view adds a reason column that translates the backend reason key.
    const skippedColumns = useMemo(() => [
        ...baseColumns,
        {
            key: 'reason',
            label: i18n.translate('restoreUsersModal.skipReason'),
            width: 350,
            render: (user) => {
                const reasonKey = get(user, 'row.reason');
                return <div className="reason-title">{i18n.translate(`restoreUsersModal.reasons.${reasonKey}`)}</div>;
            }
        }
    ], [baseColumns, i18n]);

    return <Modal
        open={open}
        onClose={showResults ? handleClose : onClose}
        data-testid="restore-confirmation-modal"
    >
        <div className="restoreUsersModal modalWrapper">
            <div className="modalTitle">
                <div>
                    {showConfirmation
                        ? i18n.translate('restoreUsersModal.title')
                        : i18n.translate('restoreUsersModal.resultsTitle')}
                </div>
            </div>

            <div className="modalContent">
                {error && (
                    <div data-testid="error-message" className="error-message">
                        {error}
                    </div>
                )}

                {showConfirmation && !error && (
                    <>
                        <div className="message">
                            {i18n.translate('restoreUsersModal.confirmMessage', {context: {userCount: selectionModel.length}})}
                        </div>
                        <div className="hint-message">
                            {i18n.translate('restoreUsersModal.confirmHint')}
                        </div>
                        <DataTable
                            columns={confirmationColumns}
                            data={usersToRestoreWithData}
                            getRowKey={(row) => row.id}
                            className="usersToRestore"
                            pageSize={10}
                            showPagination={true}
                        />
                    </>
                )}

                {showResults && !error && (
                    <>
                        {restoreResult.restored > 0 && (
                            <div className="success-message">
                                <div className="success-icon">✅</div>
                                <div className="success-text">{getResultMessage()}</div>
                            </div>
                        )}

                        {restoreResult.restored === 0 && (
                            <div className="warning-message">
                                <div className="warning-icon">⚠️</div>
                                <div className="warning-text">{getResultMessage()}</div>
                            </div>
                        )}

                        {restoreResult.restoredUsers && restoreResult.restoredUsers.length > 0 && (
                            <div className="restored-users-section">
                                <div className="section-title success">
                                    ✅ {i18n.translate('restoreUsersModal.restoredUsersTitle')} ({restoreResult.restoredUsers.length})
                                </div>
                                <DataTable
                                    columns={baseColumns}
                                    data={restoreResult.restoredUsers}
                                    getRowKey={(row) => row.userId}
                                    className="restoredUsersTable"
                                    pageSize={5}
                                    showPagination={true}
                                />
                            </div>
                        )}

                        {restoreResult.skippedUsers && restoreResult.skippedUsers.length > 0 && (
                            <div className="skipped-users-section">
                                <div className="section-title error">
                                    ⚠️ {i18n.translate('restoreUsersModal.skippedUsersTitle')} ({restoreResult.skippedUsers.length})
                                </div>
                                <div className="skipped-explanation">
                                    <div
                                        className="explanation-header"
                                        onClick={() => setIsExplanationExpanded(!isExplanationExpanded)}
                                    >
                                        <div className="explanation-title">
                                            {i18n.translate('restoreUsersModal.skippedExplanationTitle')}
                                        </div>
                                        <div className="expand-icon">
                                            {isExplanationExpanded ? '▼' : '◀'}
                                        </div>
                                    </div>
                                    {isExplanationExpanded && (
                                        <div className="explanation-text">
                                            {i18n.translate('restoreUsersModal.skippedExplanation')}
                                        </div>
                                    )}
                                </div>
                                <DataTable
                                    columns={skippedColumns}
                                    data={restoreResult.skippedUsers}
                                    getRowKey={(row) => row.userId}
                                    className="skippedUsersTable"
                                    pageSize={5}
                                    showPagination={true}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="modalFooter">
                {showConfirmation && (
                    <>
                        <Button
                            data-testid="cancel-restore-button"
                            color={buttonColors.YELLOW}
                            onClick={onClose}
                            disabled={restoring}
                            text={i18n.translate('general.close')}
                        />
                        <Button
                            data-testid="confirm-restore-button"
                            disabled={(!usersToRestoreWithData || usersToRestoreWithData.length === 0) || restoring}
                            color={buttonColors.GREY}
                            onClick={onConfirm}
                            text={restoring ? i18n.translate('restoreUsersModal.restoring') : i18n.translate('restoreUsersModal.restoreUsers')}
                        />
                    </>
                )}
                {showResults && (
                    <Button
                        data-testid="close-results-button"
                        color={buttonColors.YELLOW}
                        onClick={handleClose}
                        text={i18n.translate('general.close')}
                    />
                )}
            </div>
        </div>
    </Modal>;
};
